import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchSourceCatalog,
  type SourceCatalogEntry,
} from '../webMap/sourceCatalogService';
import {
  createSavedDashboard,
  apiCreateDashboard,
} from '@signals/app-shell/features/userContent/contentLibraryStorage';
import type { DashboardBuilderState } from './types';
import { defaultDashboardBuilderState } from './defaultState';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ready: 'success',
  syncing: 'info',
  streaming: 'info',
  connected: 'default',
  error: 'error',
  detecting: 'warning',
  mapping: 'warning',
};

function formatRelative(iso: string | null): string {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

interface DataSourcesPanelProps {
  onLibraryChanged?: () => void;
}

const DataSourcesPanel: React.FC<DataSourcesPanelProps> = ({ onLibraryChanged }) => {
  const navigate = useNavigate();
  const [sources, setSources] = useState<SourceCatalogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState(true);

  const loadSources = () => {
    setLoading(true);
    setError(null);
    fetchSourceCatalog(true)
      .then((r) => {
        if (!r.has_external_sources) {
          setAvailable(false);
          setSources([]);
        } else {
          setAvailable(true);
          setSources(r.sources);
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 503) {
          setAvailable(false);
        } else {
          setError('Failed to load data sources');
        }
        setSources([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSources(); }, []);

  const handleQuickLaunch = (src: SourceCatalogEntry) => {
    const mapId = `w_${Date.now()}`;
    const state: DashboardBuilderState = {
      ...defaultDashboardBuilderState(),
      layout: [{ i: mapId, x: 0, y: 0, w: 12, h: 8, minW: 4, minH: 4 }],
      widgets: {
        [mapId]: {
          type: 'web_map',
          config: {
            webMapOptions: {
              layers: [
                {
                  id: `wml_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                  schema: src.schema_name,
                  table: src.table_name,
                  geomColumn: src.geom_column,
                  db: 'sandbox' as const,
                  visible: true,
                  opacity: 1,
                  fillColor: '#1976d2',
                  strokeColor: '#1976d2',
                  strokeWidth: 2,
                  pointRadius: 5,
                  sourceId: src.source_id,
                  sourceName: src.source_name,
                },
              ],
              basemap: 'dark',
              initialExtent: { center: [-98.5, 39.8] as [number, number], zoom: 4 },
            },
          },
        },
      },
    };

    const dash = createSavedDashboard(src.source_name, state);
    apiCreateDashboard(dash).then(() => onLibraryChanged?.()).catch(() => { /* offline */ });
    navigate(`/dashboard-builder/${dash.id}`);
  };

  if (!available) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <StorageIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>Datasets</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            No sandbox database connected. Set SANDBOX_DATABASE_URL to a DataHog instance to see your data sources here.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <StorageIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>My Data Sources</Typography>
          </Stack>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadSources} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {loading && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading sources...</Typography>
          </Stack>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ py: 1 }}>{error}</Typography>
        )}

        {!loading && !error && sources.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data sources found. Create sources in DataHog and they will appear here.
          </Typography>
        )}

        {!loading && sources.length > 0 && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Tracks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Data</TableCell>
                  <TableCell align="center" sx={{ width: 80 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((src) => (
                  <TableRow key={src.source_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 220 }}>
                        {src.source_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {src.source_type?.replace(/_/g, ' ') ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {src.data_category ? (
                        <Chip label={src.data_category} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {src.track_count.toLocaleString()}
                      </Typography>
                      {src.unique_entities > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {src.unique_entities.toLocaleString()} entities
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={src.status ?? 'unknown'}
                        size="small"
                        color={STATUS_COLOR[src.status ?? ''] ?? 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatRelative(src.last_data_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Quick launch in Dashboard Builder">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleQuickLaunch(src)}
                        >
                          <DashboardCustomizeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default DataSourcesPanel;
